import { X, Loader2 } from "lucide-react";
import { useState, useEffect } from "react";
import fetchClient from "../services/axiosInstance";
import { IntegrationServerInstance } from "@/schema";
import { StatusModal } from "./StatusModal";

interface IntegrationSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  focusedInstanceId?: string | null;
  instance?: IntegrationServerInstance | null;
  onConnectionSuccess?: () => void;
}

/**
 * Clean selection modal for pending connectors
 * Shows "Which account to connect?" with clickable options
 */
export const IntegrationSelectionModal = ({
  isOpen,
  onClose,
  focusedInstanceId = null,
  onConnectionSuccess,
}: IntegrationSelectionModalProps) => {
  const [instance, setInstance] = useState<IntegrationServerInstance | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [statusMessage, setStatusMessage] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);

  // Fetch the instance data
  useEffect(() => {
    if (!isOpen || !focusedInstanceId) return;

    const fetchInstance = async () => {
      try {
        setLoading(true);
        const response = await fetchClient.post(
          import.meta.env.VITE_MCP_OUTLET + "/auth/getInstances"
        );
        const instances: IntegrationServerInstance[] =
          response.data.instances || [];
        const foundInstance = instances.find(
          (inst) => inst.instanceId === focusedInstanceId
        );
        setInstance(foundInstance || null);
      } catch (err) {
        console.error("Failed to fetch instance:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchInstance();
  }, [isOpen, focusedInstanceId]);

  const handleOptionClick = async (optionValue: string) => {
    if (!instance || !instance.pendingOptions) return;

    const selectedOption = instance.pendingOptions.find(
      (opt) => opt.value === optionValue
    );

    if (!selectedOption) return;

    try {
      setSubmitting(true);

      // Call the selection API
      const response = await fetchClient.post(
        import.meta.env.VITE_MCP_OUTLET + "/auth/selectOption",
        {
          instanceId: instance.instanceId,
          option: {
            value: selectedOption.value,
            label: selectedOption.label,
            metadata: selectedOption.metadata,
          },
        }
      );

      const result = response.data;
      const isSuccess = result.integrationStatus === "connected";

      // Track completion

      // Show status message
      setStatusMessage({
        type: isSuccess ? "success" : "error",
        message:
          result.message ||
          (isSuccess
            ? "Connector connected successfully!"
            : "Failed to connect connector"),
      });

      // Auto-hide and close
      setTimeout(
        () => {
          setStatusMessage(null);
          if (isSuccess) {
            onClose();
            if (onConnectionSuccess) {
              onConnectionSuccess();
            }
          }
        },
        isSuccess ? 2000 : 5000
      );
    } catch (err: any) {

      const errorMessage =
        err?.response?.data?.error ||
        err?.message ||
        "Failed to complete selection";

      setStatusMessage({
        type: "error",
        message: errorMessage,
      });

      setTimeout(() => setStatusMessage(null), 5000);
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-[var(--modal-backdrop)] backdrop-blur-sm">
        <div className="relative w-full max-w-2xl bg-[var(--modal-bg)] rounded-xl border border-white/10 shadow-2xl">
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 z-10 p-2 text-white/60 hover:text-white hover:bg-white/10 rounded-lg transition-all duration-200"
            aria-label="Close modal"
            disabled={submitting}
          >
            <X className="w-5 h-5" />
          </button>

          {/* Content */}
          <div className="p-6 sm:p-8">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-white/40 animate-spin" />
              </div>
            ) : !instance || !instance.pendingOptions ? (
              <div className="text-center py-12">
                <p className="text-white/60">No selection data available</p>
              </div>
            ) : (
              <>
                {/* Title */}
                <h2 className="text-2xl font-semibold text-white mb-2">
                  Which account to connect?
                </h2>
                <p className="text-white/60 text-sm mb-6">
                  Select an account to continue
                </p>

                {/* Options List */}
                <div className="space-y-3">
                  {instance.pendingOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => handleOptionClick(option.value)}
                      disabled={submitting}
                      className="w-full flex items-start justify-between p-4 bg-white/5 hover:bg-white/10 rounded-lg border border-white/10 hover:border-[#6B7EEA]/50 transition-all text-left disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <div className="flex-1">
                        <div className="text-white font-medium text-lg">
                          {option.label}: {option.value}
                        </div>
                      </div>
                      {submitting && (
                        <Loader2 className="w-5 h-5 text-white/40 animate-spin ml-3 flex-shrink-0" />
                      )}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Status Modal */}
      {statusMessage && (
        <StatusModal
          isOpen={!!statusMessage}
          type={statusMessage.type}
          message={statusMessage.message}
          onClose={() => setStatusMessage(null)}
          autoHideDuration={statusMessage.type === "success" ? 2000 : 5000}
        />
      )}
    </>
  );
};
